/**
 * FilterSelect — the dropdown pill used by the Performance filter row.
 *
 * One visual vocabulary for every page-level filter: a bordered pill
 * with a leading glyph, the current selection as its label, and a
 * caret. Identical chrome to `CompareSitePicker`, which sits at the
 * end of the same row — the whole strip reads as one control group
 * rather than a mix of segmented text buttons and a pill.
 *
 * Because the selection IS the label, each option's text has to stand
 * on its own without an eyebrow ("All devices", not "All"). Callers
 * own that wording.
 */
import {
  Select,
  SelectContent,
  SelectItem,
  SelectPrimitive,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { CaretDown } from '@phosphor-icons/react';
import type { ReactElement, ReactNode } from 'react';

export interface FilterSelectOption {
  value: string;
  /** Doubles as the trigger text when selected, so it has to read on
   *  its own. A node when the label carries a glyph (a region's flag). */
  label: ReactNode;
  /** Rendered before the label in the menu row (a flag, an icon). */
  prefix?: ReactNode;
}

export function FilterSelect({
  ariaLabel,
  icon,
  value,
  options,
  onChange,
  /** False when the filter sits at its "everything" default — keeps
   *  the pill quiet so an actually-narrowed view stands out. */
  active,
}: {
  ariaLabel: string;
  /** Leading glyph on the trigger. Usually fixed per filter; the
   *  audience pill swaps it for the selected device. */
  icon: ReactNode;
  value: string;
  options: readonly FilterSelectOption[];
  onChange: (next: string) => void;
  active: boolean;
}): ReactElement {
  const selected = options.find((o) => o.value === value);
  return (
    <Select
      value={value}
      onValueChange={(v) => {
        if (typeof v === 'string') onChange(v);
      }}
    >
      <SelectPrimitive.Trigger
        aria-label={ariaLabel}
        className={cn(
          'inline-flex h-7 items-center gap-1.5 rounded-md border border-[var(--color-line)] bg-[var(--color-paper)] px-2.5 text-xs outline-none transition-colors',
          active
            ? 'bg-[var(--color-paper-alt)] font-semibold text-[var(--color-ink)]'
            : 'text-[var(--color-ink-soft)]',
          'hover:bg-[var(--color-paper-alt)]/60 data-[popup-open]:bg-[var(--color-paper-alt)]',
        )}
      >
        {icon}
        <SelectValue>{selected?.label ?? value}</SelectValue>
        <SelectPrimitive.Icon>
          <CaretDown aria-hidden="true" size={10} className="text-[var(--color-mute)]" />
        </SelectPrimitive.Icon>
      </SelectPrimitive.Trigger>
      <SelectContent>
        {options.map((opt) => (
          <SelectItem key={opt.value} value={opt.value}>
            {opt.prefix ? (
              <span className="inline-flex items-center gap-2">
                {opt.prefix}
                {opt.label}
              </span>
            ) : (
              opt.label
            )}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
